import React, { useEffect, useState, memo, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogTitle,
  DialogActions, DialogContent, InputAdornment, Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import InfoButton from "../../../components/Tooltip/InfoTextField";
import DefaultActions from "../../../components/Card/DefaultActions";
import { CompanyViewModel } from "../../../Types/ExportTypes";
import {
  CountryAutoComplete, LocationAutoComplete, LanguageAutoComplete, CurrencyAutoComplete, CustomerPriceGroupAutoComplete,
  VatbusPostingGroupAutoComplete, CustomerPostingGroupAutoComplete, SupplierPostingGroupAutoComplete, Dimension1AutoComplete
} from "../../../components/AutoComplete/AutoCompletes";
import ImageBox from "../../../components/Image/ImageBox";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";

const CompanyEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { unit = "" } = params || {};

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(CompanyViewModel & { hasChanges?: boolean; originalValues?: Partial<CompanyViewModel>; }) | null>(null);
  const [showContactSettings, setShowContactSettings] = useState(false);
  const [showFinanceSettings, setShowFinanceSettings] = useState(false);
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };

  useEffect(() => {
    isLoading ? showLoading() : hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (unit) {
      setIsLoading(true);
      authService
        .get<CompanyViewModel>(`/companyApi/GetCompany?unit=${unit}`)
        .then((response) => setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null))
        .catch((error: any) => {
          showSnackbar(`${t("dialog.fetchCompanyError")} : ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultCompany: CompanyViewModel & { originalValues: Partial<CompanyViewModel>; hasChanges: boolean } = {
        unit: "",
        name: "",
        createNew: true,
        originalValues: {},
        hasChanges: false,
      } as CompanyViewModel & { originalValues: Partial<CompanyViewModel>; hasChanges: boolean };
      defaultCompany.originalValues = { ...defaultCompany };
      setViewModel(defaultCompany);
    }
  }, [unit, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateCompany()) return;

    setIsLoading(true);
    try {
      const createNew = viewModel.createNew;
      const response = await authService.post<CompanyViewModel>("/companyApi/CreateOrUpdateCompany", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccesfully"), "success");

      if (createNew === true) {
        navigate({ path: "/settings/company/edit", params: { unit: response.unit } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof CompanyViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as CompanyViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const validateCompany = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.companyDataMissing"), "error");
      return false;
    }

    if (!viewModel.unit?.trim()) {
      showSnackbar(t("dialog.unitRequired"), "warning");
      return false;
    }

    if (!viewModel.name?.trim()) {
      showSnackbar(t("dialog.nameRequired"), "warning");
      return false;
    }

    if (!viewModel.blankCurrencText?.trim()) {
      showSnackbar(t("dialog.currencyRequired"), "warning");
      return false;
    }

    if (!viewModel.defaultLocationCode?.trim()) {
      showSnackbar(t("dialog.locationRequired"), "warning");
      return false;
    }

    if (!viewModel.defaultLanguageCode?.trim()) {
      showSnackbar(t("dialog.languageRequired"), "warning");
      return false;
    }

    if (!viewModel.defaultPriceGroup?.trim()) {
      showSnackbar(t("dialog.priceGroupRequired"), "warning");
      return false;
    }

    if (!viewModel.defaultPostingGroupBus?.trim()) {
      showSnackbar(t("dialog.postingGroupWhoRequired"), "warning");
      return false;
    }

    if (!viewModel.defaultCustomerPostingGroup?.trim()) {
      showSnackbar(t("dialog.customerPostingGroupRequired"), "warning");
      return false;
    }

    if (!viewModel.defaultSupplierPostingGroup?.trim()) {
      showSnackbar(t("dialog.supplierPostingGroupRequired"), "warning");
      return false;
    }





    return true;
  };


  const handleCopy = async (newUnit?: string) => {
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      showSnackbar(t("dialog.copyPreliminaryCompany"), "warning");
      return;
    }

    if (newUnit && newUnit === viewModel?.unit) {
      showSnackbar(t("dialog.unitSameAsSource"), "warning");
      return;
    }
    // If newCode is provided, update viewModel and continue
    if (newUnit) {
      setViewModel(prev => ({ ...prev, newUnit }));
    }


    // If newCode isn't set, open the modal and return
    if (!viewModel?.newUnit && !newUnit) {
      setIsCopyModalOpen(true);
      return;
    }
    setIsCopyModalOpen(false);
    setIsLoading(true);
    try {
      var response = await authService.post<CompanyViewModel>(`/companyApi/CopyCompany`, { ...viewModel, newUnit, copyFromUnit: viewModel?.unit ?? "" });
      setViewModel(response);
      showSnackbar(t("dialog.copySuccess"), "success");

      navigate({ path: "/settings/company/edit", params: { unit: response.unit } });

    } catch (error: any) {

      showSnackbar(`${t("dialog.copyError")} : ${error.response?.data ?? error.message}`, "error");
      setViewModel(prev => ({ ...prev, newCode: null }));

    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/company/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/companyApi/DeleteCompany`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");

      navigate({ path: "/settings/company/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.failedDelete")}: ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (viewModel?.createNew == true) {
      setPendingImage(file);
      setPendingImageUrl(URL.createObjectURL(file));
    } else {
      uploadImage(file, viewModel?.unit);
    }
  };

  const uploadImage = async (file: File, unit?: string | null) => {
    try {
      if (!unit) return;
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("unit", unit);

      const response = await authService.post<{ url: string }>("/companyApi/UploadImageForCompany", formData);
      if (response?.url) {
        setViewModel((prev) => ({ ...prev, cdnImageLink: response.url }));
        showSnackbar(t("dialog.imageUploaded"), "success");
      } else {
        throw new Error("Failed to upload image.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showSnackbar(t("dialog.imageUploadError"), "error");
    } finally {
      setIsLoading(false);
    }
  };


  type UnitInputModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (newCode: string) => void;
    ViewModel?: CompanyViewModel | null
  };
  const UnitInputModal = memo(({ open, onClose, onConfirm, ViewModel }: UnitInputModalProps) => {
    const [tempUnit, setTempUnit] = useState("");
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{t("common.enterNewUnit")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("common.newUnit")}
            fullWidth
            variant="outlined"
            value={tempUnit}
            onChange={(e) => setTempUnit(e.target.value)} // Only updates local state
            error={ViewModel?.unit === tempUnit}
            helperText={
              ViewModel?.unit === tempUnit ? t("help.unitSameAsSource") : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (!tempUnit.trim()) {
                showSnackbar(t("dialog.unitEmpty"), "warning");
                return;
              }
              onConfirm(tempUnit?.trim()); // Pass only on confirm
            }}
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  return (
    <>
      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.company")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-start", maxWidth: "fit-content", mb: 2 }}>
          <DefaultActions onSave={handleSave} onDelete={handleDelete} onCopy={handleCopy} />
        </Box>
        <UnitInputModal open={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onConfirm={handleCopy} ViewModel={viewModel} />
        {viewModel && (
          <Grid container spacing={2}>
            {viewModel.createNew === true && (
              <Grid item xs={12}>
                <TextField
                  label={t("common.unit")}
                  value={viewModel.unit || ""}
                  onChange={(e) => handleChange("unit", e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={!viewModel.createNew}
                  error={!viewModel.unit || viewModel.unit.trim() === ""}
                  helperText={
                    !viewModel.unit?.trim() ? t("help.unitRequired") : ""
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label={t("common.name")}
                value={viewModel.name || ""}
                onChange={(e) => handleChange("name", e.target.value)}
                fullWidth
                margin="normal"
                error={!viewModel.name || viewModel.name.trim() === ""}
                helperText={!viewModel.name || viewModel.name.trim() === "" ? t("help.nameRequired") : ""}
              />
            </Grid>


            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <LocationAutoComplete
                  key={viewModel.defaultLocationCode || null}
                  valueId={viewModel.defaultLocationCode || null}
                  onChange={(selected) => handleChange("defaultLocationCode", selected?.value || "")}
                  infoText={t("help.defaultLocation")}
                  error={!viewModel.defaultLocationCode || viewModel.defaultLocationCode.trim() === ""}
                  helperText={
                    !viewModel.defaultLocationCode?.trim() ? t("help.defaultLocationRequired") : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <LanguageAutoComplete
                  key={viewModel.defaultLanguageCode || null}
                  valueId={viewModel.defaultLanguageCode || null}
                  onChange={(selected) => handleChange("defaultLanguageCode", selected?.value || "")}
                  infoText={t("help.defaultLanguage")}
                  error={!viewModel.defaultLanguageCode || viewModel.defaultLanguageCode.trim() === ""}
                  helperText={
                    !viewModel.defaultLanguageCode?.trim() ? t("help.defaultLanguageRequired") : ""
                  }
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <CurrencyAutoComplete
                  key={viewModel.blankCurrencText || null}
                  valueId={viewModel.blankCurrencText || null}
                  onChange={(selected) => handleChange("blankCurrencText", selected?.value || "")}
                  infoText={t("help.defaultCurrency")}
                  disabled={!viewModel.createNew}
                  error={!viewModel.blankCurrencText || viewModel.blankCurrencText.trim() === ""}
                  helperText={
                    !viewModel.blankCurrencText?.trim() ? t("help.defaultCurrencyRequired") : ""
                  }
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <CustomerPriceGroupAutoComplete
                  key={viewModel.defaultPriceGroup || null}
                  valueId={viewModel.defaultPriceGroup || null}
                  onChange={(selected) => handleChange("defaultPriceGroup", selected?.value || "")}
                  infoText={t("help.defaultPriceGroup")}
                  error={!viewModel.defaultPriceGroup || viewModel.defaultPriceGroup.trim() === ""}
                  helperText={
                    !viewModel.defaultPriceGroup?.trim() ? t("help.defaultPriceGroupRequired") : ""
                  }

                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <VatbusPostingGroupAutoComplete
                  key={viewModel.defaultPostingGroupBus || null}
                  valueId={viewModel.defaultPostingGroupBus || null}
                  onChange={(selected) => handleChange("defaultPostingGroupBus", selected?.value || "")}
                  infoText={t("help.defaultPostingGroupWho")}
                  error={!viewModel.defaultPostingGroupBus || viewModel.defaultPostingGroupBus.trim() === ""}
                  helperText={
                    !viewModel.defaultPostingGroupBus?.trim() ? t("help.defaultPostingGroupWhoRequired") : ""
                  }

                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <CustomerPostingGroupAutoComplete
                  key={viewModel.defaultCustomerPostingGroup || null}
                  valueId={viewModel.defaultCustomerPostingGroup || null}
                  onChange={(selected) => handleChange("defaultCustomerPostingGroup", selected?.value || "")}
                  infoText={t("help.defaultCustomerPostingGroup")}
                  error={!viewModel.defaultCustomerPostingGroup || viewModel.defaultCustomerPostingGroup.trim() === ""}
                  helperText={
                    !viewModel.defaultCustomerPostingGroup?.trim() ? t("help.defaultCustomerPostingGroupRequired") : ""
                  }
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <SupplierPostingGroupAutoComplete
                  key={viewModel.defaultSupplierPostingGroup || null}
                  valueId={viewModel.defaultSupplierPostingGroup || null}
                  onChange={(selected) => handleChange("defaultSupplierPostingGroup", selected?.value || "")}
                  infoText={t("help.defaultSupplierPostingGroup")}
                  error={!viewModel.defaultSupplierPostingGroup || viewModel.defaultSupplierPostingGroup.trim() === ""}
                  helperText={
                    !viewModel.defaultSupplierPostingGroup?.trim()
                      ? t("help.defaultSupplierPostingGroupRequired")
                      : ""
                  }

                />
              </FormControl>
            </Grid>



            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!viewModel.testCompany}
                    onChange={(e) => handleChange("testCompany", e.target.checked)}
                  />
                }
                label={t("common.testCompany")}
              />
            </Grid>


            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showContactSettings} onChange={(e) => setShowContactSettings(e.target.checked)} />}
                label={t("common.showContactSettings")}
              />
            </Grid>


            {showContactSettings && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%"
                  }}
                >
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.email")}
                      value={viewModel.eMail || ""}
                      onChange={(e) => handleChange("eMail", e.target.value)}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <InfoButton infoText={t("help.primaryEmail")} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.phoneNo")}
                      value={viewModel.phoneNo || ""}
                      onChange={(e) => handleChange("phoneNo", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.address")}
                      value={viewModel.address || ""}
                      onChange={(e) => handleChange("address", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.city")}
                      value={viewModel.city || ""}
                      onChange={(e) => handleChange("city", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>


                  <Grid item xs={12}>
                    <TextField
                      label={t("common.county")}
                      value={viewModel.county || ""}
                      onChange={(e) => handleChange("county", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <CountryAutoComplete
                        key={viewModel.countryCode || null}
                        valueId={viewModel.countryCode || null}
                        onChange={(selected) => handleChange("countryCode", selected?.value || "")}
                        infoText={t("help.companyCountry")}

                      />
                    </FormControl>
                  </Grid>


                  <Grid item xs={12}>
                    <TextField
                      label={t("common.seat")}
                      value={viewModel.registeredOffice || ""}
                      onChange={(e) => handleChange("registeredOffice", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.phoneNo")}
                      value={viewModel.phoneNo || ""}
                      onChange={(e) => handleChange("phoneNo", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.email")}
                      value={viewModel.eMail || ""}
                      onChange={(e) => handleChange("eMail", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.customerServicePhone")}
                      value={viewModel.custServicePhone || ""}
                      onChange={(e) => handleChange("custServicePhone", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.customerServiceEmail")}
                      value={viewModel.custServiceEmail || ""}
                      onChange={(e) => handleChange("custServiceEmail", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>


                  <Grid item xs={12}>
                    <TextField
                      label={t("common.senderEmail")}
                      value={viewModel.emailSenderAddress || ""}
                      onChange={(e) => handleChange("emailSenderAddress", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.website")}
                      value={viewModel.homePage || ""}
                      onChange={(e) => handleChange("homePage", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>


                </Box>
              </>
            )}


            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showFinanceSettings} onChange={(e) => setShowFinanceSettings(e.target.checked)} />}
                label={t("common.showFinanceSettings")}
              />
            </Grid>
            {showFinanceSettings && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%",
                    mb: 2
                  }}
                >
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.orgNo")}
                      value={viewModel.orgNo || ""}
                      onChange={(e) => handleChange("orgNo", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.vatNo")}
                      value={viewModel.vatNo || ""}
                      onChange={(e) => handleChange("vatNo", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>


                </Box>
              </>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* Current or Temporary Image Preview */}
            <ImageBox
              imageUrl={pendingImage ? pendingImageUrl ?? "" : viewModel?.cdnImageLink ?? ""}
              altText={viewModel?.unit ?? ""}
              fallbackText={t("common.noImage")}
              width="300px"
            />

            {/* Image Upload Input */}
            <Button
              variant="contained"
              component="label"
              sx={{ textTransform: "none", maxWidth: "250px", mt: 2 }}
            >
              {t("common.uploadImage")}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </Box>

        </Grid>

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>

    </>
  );
};

export default CompanyEditPage;
